import React from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, LockIcon, MailIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { ContactForm } from '../components/ContactForm';
import { services } from '../data/services';
import { company } from '../data/company';
const assurances = [{
  icon: MessageSquareIcon,
  title: 'A straightforward conversation',
  text: 'We start by understanding the situation and the responsibilities involved — no obligation.'
}, {
  icon: LockIcon,
  title: 'Handled privately',
  text: 'Inquiries are treated discreetly and shared only with the people who need to be involved.'
}, {
  icon: ClockIcon,
  title: 'A clear next step',
  text: 'We will explain which services apply and what working together would look like.'
}];
export function Contact() {
  return <>
      <PageHeader eyebrow="Contact Us" title="Let's Discuss How We Can Help" description="Every individual and family has different needs. Contact us to discuss the type of fiduciary or administrative support you require." crumbs={[{
      label: 'Contact Us'
    }]} data-id="element-256" data-mp="aalemt1el6zh" />

      <section className="bg-navy-50" data-id="element-257" data-mp="1e8accz7b8l0n">
        <div className="mx-auto grid max-w-content gap-12 px-6 py-16 sm:py-20 lg:grid-cols-[1.3fr_0.7fr] lg:gap-16 lg:px-8" data-id="element-258" data-mp="1j6typ0ptfytj">
          <ContactForm data-id="element-259" data-mp="mh2wsgf739dp" />

          <aside className="flex flex-col gap-10" data-id="element-260" data-mp="mhw1lbulfot7">
            <div className="bg-navy-950 p-7" data-id="element-261" data-mp="qi658huwy34w">
              <h2 className="text-[12px] uppercase tracking-[0.2em] text-gold-300" data-id="element-262" data-mp="qixu79u092m7">
                Contact Details
              </h2>
              <ul className="mt-6 space-y-5 text-[15px] text-navy-200" data-id="element-263" data-mp="vjluyn81cxtm">
                <li className="flex items-start gap-3" data-id="element-264" data-mp="10hx38tion09q">
                  <PhoneIcon className="mt-1 h-4 w-4 shrink-0 text-gold-300" aria-hidden="true" data-id="element-265" data-mp="26wxs6f9aii6t" />
                  <a href={company.phoneHref} className="transition-colors duration-150 ease-out hover:text-white" data-id="element-266" data-mp="2bxlsjrjesbdp">
                    {company.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-267" data-mp="1w4hztj82pkzc">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-gold-300" aria-hidden="true" data-id="element-268" data-mp="1r6gj34y4dxr1" />
                  <a href={company.emailHref} className="break-all transition-colors duration-150 ease-out hover:text-white" data-id="element-269" data-mp="13hnk0zm7b9s7">
                    {company.email}
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-270" data-mp="18ff9zo3acltr">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-gold-300" aria-hidden="true" data-id="element-271" data-mp="18enlsyc0pqdi" />
                  <address className="not-italic leading-relaxed" data-id="element-272" data-mp="fs2xk6qu9ue6">
                    {company.addressLine1}
                    <br data-id="element-273" data-mp="2rdljpndfew5h" />
                    {company.addressLine2}
                  </address>
                </li>
                <li className="flex items-start gap-3" data-id="element-274" data-mp="2w6xx11cnngix">
                  <ClockIcon className="mt-1 h-4 w-4 shrink-0 text-gold-300" aria-hidden="true" data-id="element-275" data-mp="2hgz1758koh6m" />
                  <span className="leading-relaxed" data-id="element-276" data-mp="2mdh3lygfk3fs">{company.hours}</span>
                </li>
              </ul>
            </div>

            <div data-id="element-277" data-mp="3f3z5iw5f3yrm">
              <h2 className="font-serif text-[22px] text-navy-950" data-id="element-278" data-mp="3f4h96duetf9k">
                What happens next
              </h2>
              <ul className="mt-6 space-y-6" data-id="element-279" data-mp="313oe9s074i5x">
                {assurances.map(item => <li key={item.title} className="flex gap-4" data-id="element-280" data-mp="35502pu7c2xcj">
                    <item.icon className="mt-1 h-5 w-5 shrink-0 text-gold-500" aria-hidden="true" data-id="element-281" data-mp="1ppj6sp0rxq5u" />
                    <div data-id="element-282" data-mp="1ts7dor5xgn06">
                      <h3 className="text-[15px] font-medium text-navy-900" data-id="element-283" data-mp="29y0we7mebfq7">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-[14px] leading-relaxed text-navy-600" data-id="element-284" data-mp="2569c2etu268i">
                        {item.text}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="border-t border-navy-200 pt-8" data-id="element-285" data-mp="1v7a6j54d6qpq">
              <h2 className="text-[12px] uppercase tracking-[0.2em] text-gold-600" data-id="element-286" data-mp="2swd5jtdb4pls">
                Our Services
              </h2>
              <ul className="mt-5 space-y-2.5" data-id="element-287" data-mp="2nvz9zasw784n">
                {services.map(service => <li key={service.slug} data-id="element-288" data-mp="2j0jxam6mfi9t">
                    <Link to={service.path} className="text-[15px] text-navy-700 transition-colors duration-150 ease-out hover:text-navy-950" data-id="element-289" data-mp="2exvkk3esdr7k">
                      {service.name}
                    </Link>
                  </li>)}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>;
}